import { Marked, Token, Tokens, TokensList } from 'marked';
import { TRUNCATION_ELLIPSIS, wordBoundaryCut } from './chat-truncate.util';

export interface IChatMarkdownTruncated {
  // The shortened message as Markdown, ending with the ellipsis; empty when the message fits.
  content: string;
  isTruncated: boolean;
}

const BLOCK_CONTAINER_TYPES = ['blockquote'];
const INLINE_CONTAINER_TYPES = ['paragraph', 'heading', 'text'];
const LIST_ITEM_MARKER = /^ {0,3}([*+-]|\d{1,9}[.)])/;
const CODE_FENCE = /^ {0,3}(`{3,}|~{3,})/;
const STYLE_MARKERS: Record<string, RegExp> = { strong: /^(\*\*|__)/, em: /^[*_]/, del: /^~~?/ };
const BLOCK_SEPARATOR = '\n\n';

// Own instance: ngx-markdown configures the global `marked`, which must not leak into this one or back.
const chatMarked = new Marked();

/**
 * Cuts marked's tokens to a number of visible characters and writes what is kept back as Markdown. Each block after the
 * first costs one character, like the line break between them; where the cut happens, the ellipsis is inserted.
 */
class ChatMarkdownTruncator {
  private remaining: number;
  private hasContent = false;
  private isCut = false;
  private readonly ellipsis: Tokens.Text = { type: 'text', raw: TRUNCATION_ELLIPSIS, text: TRUNCATION_ELLIPSIS };
  // Tokens rebuilt by the cut; every other token is written back from its own source (`raw`).
  private readonly rebuilt = new Set<Token>();

  public constructor(limit: number) {
    this.remaining = limit;
  }

  /** The shortened Markdown, or null when everything fits. */
  public truncate(tokens: TokensList): string | null {
    const truncated = this.truncateTokens(tokens, true);
    if (!this.isCut) {
      return null;
    }
    // Reference links keep working although their definitions may come after the cut.
    const definitions = Object.entries(tokens.links).map(
      ([label, { href, title }]) => `[${label}]: <${href}>${title ? ` "${title.replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"` : ''}`,
    );
    return [this.writeBlocks(truncated, BLOCK_SEPARATOR), ...definitions].join(BLOCK_SEPARATOR);
  }

  private truncateTokens(tokens: Token[], isBlockLevel: boolean): Token[] {
    const kept: Token[] = [];
    for (const token of tokens) {
      if (token.type === 'space' || token.type === 'def') {
        continue;
      }
      if (isBlockLevel && kept.length) {
        this.remaining--;
      }
      const truncated = this.truncateToken(token);
      // A block or style left with nothing but the ellipsis is dropped; the ellipsis follows what came before instead.
      if (this.isCut && this.isEllipsisOnly(truncated)) {
        if (isBlockLevel && kept.length) {
          this.appendEllipsis(kept);
        } else {
          kept.push(this.ellipsis);
        }
        break;
      }
      kept.push(...truncated);
      if (this.isCut) {
        break;
      }
    }
    return kept;
  }

  private truncateToken(token: Token): Token[] {
    switch (token.type) {
      case 'list':
        return [this.truncateList(token as Tokens.List)];
      case 'table':
        return [this.truncateTable(token as Tokens.Table)];
      case 'br':
      case 'hr':
        return this.takeFixed(token);
      case 'link':
        return this.takeWhole(token, token.text.length);
      case 'html':
        return this.takeWhole(token, token.raw.length);
      case 'image':
        return [token];
      default:
        if ('tokens' in token && token.tokens?.length) {
          const tokens = this.truncateTokens(token.tokens, BLOCK_CONTAINER_TYPES.includes(token.type));
          return [this.isCut ? this.rebuild({ ...token, tokens } as Token) : token];
        }
        return this.takeText(token);
    }
  }

  // Leaf tokens with text (text, escape, codespan, code): kept whole, or cut at a word boundary and followed by the ellipsis.
  private takeText(token: Token): Token[] {
    const text: string = 'text' in token ? token.text : '';
    if (text.length <= this.remaining) {
      this.remaining -= text.length;
      this.hasContent ||= text.trim().length > 0;
      return [token];
    }

    // Only a single word longer than the whole limit is cut mid-word, so the message never collapses to nothing.
    const cut = wordBoundaryCut(text, this.remaining) || (this.hasContent ? 0 : Math.max(this.remaining, 0));
    this.isCut = true;
    const kept = text.slice(0, cut).trimEnd();
    return [...(kept ? [{ ...token, raw: this.writeText(token, kept), text: kept } as Token] : []), this.ellipsis];
  }

  // Links and raw HTML are kept whole or dropped, so a link is never split and a tag is never cut open.
  private takeWhole(token: Token, length: number): Token[] {
    if (length <= this.remaining) {
      this.remaining -= length;
      return [token];
    }
    this.isCut = true;
    return [this.ellipsis];
  }

  // Tokens without text (line break, rule) cost one character.
  private takeFixed(token: Token): Token[] {
    if (this.remaining < 1) {
      this.isCut = true;
      return [this.ellipsis];
    }
    this.remaining--;
    return [token];
  }

  private truncateList(token: Tokens.List): Tokens.List {
    const kept: Tokens.ListItem[] = [];
    for (const item of token.items) {
      if (kept.length) {
        this.remaining--;
      }
      const tokens = this.truncateTokens(item.tokens, true);
      // An item left with nothing but the ellipsis is dropped; the ellipsis moves to the end of the previous item instead.
      if (this.isCut && kept.length && this.isEllipsisOnly(tokens)) {
        this.appendEllipsis(kept);
        break;
      }
      kept.push(this.isCut ? this.rebuild({ ...item, tokens }) : item);
      if (this.isCut) {
        break;
      }
    }
    return this.isCut ? this.rebuild({ ...token, items: kept }) : token;
  }

  // Rows are kept while they fit; a row cut in the middle keeps its columns, with the cells after the cut left empty.
  private truncateTable(token: Tokens.Table): Tokens.Table {
    const truncateRow = (row: Tokens.TableCell[]): Tokens.TableCell[] =>
      row.map((cell) => {
        if (this.isCut) {
          return { ...cell, text: '', tokens: [] };
        }
        this.remaining--;
        return { ...cell, tokens: this.truncateTokens(cell.tokens, false) };
      });

    const header = truncateRow(token.header);
    const rows: Tokens.TableCell[][] = [];
    for (const row of token.rows) {
      if (this.isCut) {
        break;
      }
      rows.push(truncateRow(row));
    }
    return this.isCut ? this.rebuild({ ...token, header, rows }) : token;
  }

  private isEllipsisOnly(tokens: Token[]): boolean {
    if (tokens.length !== 1) {
      return false;
    }
    const [token] = tokens;
    if (token.type === 'list') {
      return this.isEllipsisOnly(token.items);
    }
    return token === this.ellipsis || ('tokens' in token && !!token.tokens && this.isEllipsisOnly(token.tokens));
  }

  // Appends the ellipsis at the end of the last kept block: inside its text when it has any (paragraph, heading, list item
  // text), into its last item for a list or list items, otherwise after it.
  private appendEllipsis(blocks: Token[]): void {
    const last = blocks[blocks.length - 1];
    if (last?.type === 'list' && last.items?.length) {
      const items = [...(last.items as Tokens.ListItem[])];
      this.appendEllipsis(items);
      blocks[blocks.length - 1] = this.rebuild({ ...last, items } as Token);
    } else if (last?.type === 'list_item') {
      const tokens = [...(last.tokens as Token[])];
      this.appendEllipsis(tokens);
      blocks[blocks.length - 1] = this.rebuild({ ...last, tokens } as Token);
    } else if (last && 'tokens' in last && last.tokens && INLINE_CONTAINER_TYPES.includes(last.type)) {
      blocks[blocks.length - 1] = this.rebuild({ ...last, tokens: [...last.tokens, this.ellipsis] } as Token);
    } else {
      blocks.push(this.ellipsis);
    }
  }

  private rebuild<T extends Token>(token: T): T {
    this.rebuilt.add(token);
    return token;
  }

  // Source of a leaf cut short: a code span or code block keeps its fences, plain text is written as is.
  private writeText(token: Token, kept: string): string {
    if (token.type === 'codespan') {
      const fence = /^`+/.exec(token.raw)?.[0] ?? '`';
      return `${fence} ${kept} ${fence}`;
    }
    if (token.type === 'code') {
      const [opening] = token.raw.split('\n');
      const fence = CODE_FENCE.exec(opening)?.[1];
      return fence ? `${opening}\n${kept}\n${fence}` : kept.replace(/^/gm, '    ');
    }
    return kept;
  }

  private writeBlocks(tokens: Token[], separator: string): string {
    return tokens.map((token) => this.write(token).trimEnd()).join(separator);
  }

  private writeInline(tokens: Token[]): string {
    return tokens.map((token) => this.write(token)).join('');
  }

  private write(token: Token): string {
    if (!this.rebuilt.has(token)) {
      return token.raw;
    }
    switch (token.type) {
      case 'heading':
        return `${'#'.repeat(token.depth)} ${this.writeInline(token.tokens ?? [])}`;
      case 'paragraph':
      case 'text':
        return this.writeInline(token.tokens ?? []);
      case 'strong':
      case 'em':
      case 'del': {
        const marker = STYLE_MARKERS[token.type].exec(token.raw)?.[0] ?? '';
        return `${marker}${this.writeInline(token.tokens ?? [])}${marker}`;
      }
      case 'blockquote':
        return this.writeBlocks(token.tokens ?? [], BLOCK_SEPARATOR).replace(/^/gm, '> ');
      case 'list':
        return (token as Tokens.List).items.map((item) => this.write(item).trimEnd()).join(token.loose ? BLOCK_SEPARATOR : '\n');
      case 'list_item':
        return this.writeListItem(token as Tokens.ListItem);
      case 'table':
        return this.writeTable(token as Tokens.Table);
      default:
        return token.raw;
    }
  }

  // The item's own marker (and task checkbox), its content indented under it as Markdown expects.
  private writeListItem(item: Tokens.ListItem): string {
    // Written as bullet or number plus one space, whatever whitespace (tabs included) followed it originally.
    const marker = `${LIST_ITEM_MARKER.exec(item.raw)?.[1] ?? '-'} `;
    const checkbox = item.task ? `[${item.checked ? 'x' : ' '}] ` : '';
    const content = this.writeBlocks(item.tokens, item.loose ? BLOCK_SEPARATOR : '\n');
    return `${marker}${checkbox}${content.replace(/\n(?!$)/g, `\n${' '.repeat(marker.length)}`)}`;
  }

  // Cells are written back as Markdown, so their backslashes are escapes to keep; only `|`, which marked unescapes inside a
  // cell, is escaped again so it does not split the cell.
  private writeTable(token: Tokens.Table): string {
    const writeRow = (row: Tokens.TableCell[]): string =>
      `| ${row.map((cell) => this.writeInline(cell.tokens).split('|').join('\\|')).join(' | ')} |`;
    const delimiter = token.raw.split('\n')[1];
    return [writeRow(token.header), delimiter, ...token.rows.map(writeRow)].join('\n');
  }
}

/**
 * Shortens Markdown to `limit` visible characters the WhatsApp way: cut after parsing, never mid-word, so formatting stays
 * intact and links keep their href. The result is Markdown again, rendered by <markdown> like the full message.
 */
export function truncateChatMarkdown(content: string, limit: number): IChatMarkdownTruncated {
  // Visible characters never outnumber the source ones, so a message this short is never cut.
  if (content.length <= limit) {
    return { content: '', isTruncated: false };
  }
  const truncated = new ChatMarkdownTruncator(limit).truncate(chatMarked.lexer(content));
  return truncated === null ? { content: '', isTruncated: false } : { content: truncated, isTruncated: true };
}
